import React, { PropsWithChildren, ReactElement } from "react";
import { DropdownRecord, fromJSON as dropdownFromJSON } from "common/components/Dropdown/Dropdown";
import { FormContext } from "common/components/Form/FormContext";
import { useSelector } from "react-redux";
import { DropdownData } from "common/components/Dropdown/Data/DropdownData";
import { RootState } from "common/reducers";
import { debounce } from "common/helpers/Async";
import { IFieldBaseProps } from "common/components/Field";
import { InputActionMeta } from "react-select";
import { Collection, List } from "immutable";
import { IdType, Nullable } from "common/types";
import { byId, getRecordsByModel, loading as loadingSelector } from "common/selectors/idsManagement.selectors";
import { useAppSelector } from "common/hooks/storeHooks";
import { parsePath } from "common/helpers/FormPath";

export interface IQueryableDropdownProps extends IFieldBaseProps<"queryableDropdown" | "queryableMultiDropdown"> {
  modelPath: (string | number)[];
  query: (queryTerm: Nullable<string>, modelName: string) => void | Promise<void>;
  allOptionId?: Nullable<IdType>;
  allOptionLabel?: string;
  nameFn?: (item: any) => string;
  validate?: (value: DropdownRecord | List<DropdownRecord>) => Nullable<string>;
  queryOnLoad?: boolean;
  placeholder: string;
  modelName: string;
  onChange?: (value: DropdownRecord | List<DropdownRecord>) => void;
}

/**
 *  QueryableDropdown component
 */
export const QueryableDropdown = ({
  id,
  label,
  name,
  modelPath,
  validate,
  query,
  queryOnLoad,
  allOptionId,
  allOptionLabel,
  nameFn,
  type,
  disabled,
  placeholder,
  modelName,
  onChange,
}: PropsWithChildren<IQueryableDropdownProps>): ReactElement<any> => {
  // Hooks
  React.useEffect(() => {
    if (queryOnLoad) {
      onQuery("");
    }
  }, []);
  const { rootPath } = React.useContext(FormContext);

  // Local State
  const [lastInputValue, setLastInputValue] = React.useState("");
  const path = parsePath(name);
  const selected: (string | number) | List<string | number> = useAppSelector(
    (state) => state.getIn(rootPath.concat(path)) as any,
  );
  let isMulti = false;
  let closeMenuOnSelect = true;
  if (type === "queryableMultiDropdown") {
    isMulti = true;
    closeMenuOnSelect = false;
  }

  // Selectors
  const queriedOptions = useAppSelector((state) => {
    let items = getRecordsByModel(state as any, { path: modelPath, modelName });

    if (nameFn) {
      items = items.map((item) => dropdownFromJSON({ id: item.id, name: nameFn(item) }));
    }

    if (allOptionLabel) {
      const allOption = dropdownFromJSON({ id: allOptionId, name: allOptionLabel });
      items = items.insert(0, allOption);
    }

    if (!selected) {
      return items;
    }

    const findReduxItem = (itemValue: string | number) => {
      const record = byId(state, { path: modelPath }).get(itemValue);
      if (!record) {
        console.error(`Selected item not found in redux store ${itemValue}`);
        return null;
      }

      let recordName = record?.get("name");
      if (nameFn) {
        recordName = nameFn(record);
      }

      return dropdownFromJSON({ id: record.id, name: recordName });
    };

    // Always place selected item at the top of the list
    if (isMulti) {
      const selectedRecords = (selected as List<string | number>).map((s) => findReduxItem(s));

      // Remove selected items
      items = items.filter((item) => !(selected as List<string | number>).includes(item.id));
      // Add selected items at the top
      items = selectedRecords.filter((sr) => sr).merge(items);
    } else {
      const selectedRecord = findReduxItem(selected as string | number);

      // Do nothing if the selected item is NOT found
      if (selectedRecord) {
        // Remove selected item
        const filtered = items.filter((item) => item.id !== selected);

        // Could be a List or a Collection.Indexed
        if ((filtered as Collection.Indexed<DropdownRecord>).toList) {
          // Insert selected item at the top
          items = filtered.toList().insert(0, selectedRecord);
        } else {
          // Insert selected item at the top
          items = filtered.insert(0, selectedRecord);
        }
      }
    }

    return items;
  });

  const loading = useSelector((state: RootState) => loadingSelector(state, { modelName }));

  // Methods
  const handleInputChange = debounce((term: string, { action }: InputActionMeta) => {
    if (action === "menu-close") {
      return;
    }
    if (lastInputValue !== term) {
      onQuery(term);
    }
    setLastInputValue(term);
  }, 500);

  const onQuery = (term: string) => {
    query(term, modelName);
  };

  const onMenuOpen = () => {
    onQuery("");
  };

  const onValueChange = (selected: List<DropdownRecord>) => {
    if (!selected) return;

    if (onChange) {
      onChange(selected);
    }
  };

  const filterOption = () => true;

  return (
    <DropdownData
      id={id}
      label={label}
      name={name}
      disabled={!!disabled}
      validate={validate}
      isMulti={isMulti}
      closeMenuOnSelect={closeMenuOnSelect}
      isLoading={loading}
      options={queriedOptions}
      onQueryInputChange={handleInputChange}
      onValueChange={onValueChange}
      selectProps={{
        onMenuOpen,
        filterOption,
      }}
      placeholder={placeholder}></DropdownData>
  );
};
